"use client"

// import node module libraries
import { useState, useEffect, useRef, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  deleteUserActionManager,
  getAllUsersAction,
  updateUserActionManager,
} from "../../../../services/action/manager"
import FullScreenModal from "../../../utils/modals/FullScreenModal"
import PersonalCard from "../../../card/PersonalCard"
import DeleteConfirmation from "../../../utils/dialoge/DeleteConfirmation"
import FormEditExpert from "./components/FormEditExpert"
import {
  Plus,
  User,
  Mail,
  Briefcase,
  ChevronDown,
  ChevronUp,
  Users,
  Activity,
  UserCheck,
  Clock,
  ArrowUpDown,
  Menu,
  Download,
} from "lucide-react"
import { returnDateWithTime } from "../../../utils/functions/functions"
import socket from "../../../socket/socket"
import { handlePopupModalAction } from "../../../../services/action/event"

// Custom Toggle Switch Component
const ToggleSwitch = ({ checked, onChange, size = "default" }) => {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input type="checkbox" className="sr-only peer" checked={checked} onChange={onChange} />
      <div
        className={`
          ${size === "small" ? "w-8 h-4" : "w-11 h-6"} 
          rounded-full 
          peer 
          transition-all
          duration-200
          shadow-inner
          ${checked ? "bg-gradient-to-r from-green-400 to-green-600" : "bg-gray-200"}
          after:content-[''] 
          after:absolute 
          after:top-[2px] 
          after:left-[2px] 
          after:bg-white 
          after:rounded-full 
          after:h-${size === "small" ? "3" : "5"} 
          after:w-${size === "small" ? "3" : "5"} 
          after:shadow-md
          after:transition-all
          after:duration-300
          peer-checked:after:translate-x-full
          ${checked ? "after:border-green-200" : "after:border-gray-200"}
        `}
      ></div>
    </label>
  )
}

// Export to CSV function
const exportToCSV = (data, filename = "experts-data.csv") => {
  if (!data || !data.length) {
    console.warn("No data to export")
    return
  }

  // Define the headers for the CSV
  const headers = ["Name", "Email", "Number", "Role", "Status", "Last Login", "Online Status"]

  // Map the data to CSV rows
  const csvRows = data.map((expert) => [
    expert.name || "NA",
    expert.email || "NA",
    expert.number || "NA",
    expert.userType || "NA",
    expert.status || "NA",
    returnDateWithTime(expert.lastLogin) || "NA",
    expert.isOnline ? "Online" : "Offline",
  ])

  // Combine headers and rows
  const csvContent = [
    headers.join(","),
    ...csvRows.map((row) =>
      row
        .map(
          (cell) =>
            // Escape commas and quotes in the data
            `"${String(cell).replace(/"/g, '""')}"`,
        )
        .join(","),
    ),
  ].join("\n")

  // Create a blob and download link
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.setAttribute("href", url)
  link.setAttribute("download", filename)
  link.style.visibility = "hidden"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const ExpertListComponent = ({ listTitle = "Expert List" }) => {
  const [pageData, setPageData] = useState({
    page: 1,
    limit: 15,
  })
  const { userList: TeamsData } = useSelector((state) => state.managerReducer)

  const { isDeleteUser } = useSelector((state) => state.managerReducer)
  const { user } = useSelector((state) => state.authenticationReducer)
  const dispatch = useDispatch()
  const [operatorDetail, setExpertDetails] = useState({
    modal: false,
    preview: false,
    edit: false,
    data: "",
  })
  const [deleteId, setDeleteId] = useState("")
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 0)
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false)
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  })
  const [sortedData, setSortedData] = useState([])
  const [showActiveOnly, setShowActiveOnly] = useState(false)
  const [search, setSearch] = useState("")

  const getList = ({ limit = pageData?.limit, currentPage, firstTime = true, keyword = search }) => {
    dispatch(
      getAllUsersAction({
        userType: user?.userType,
        firstTime,
        data: { limit, userType: "expert", page: currentPage, keyword },
      }),
    )
  }

  useEffect(() => {
    socket.on("internal_user_activity", ({ userType }) => {
      console.log("client llist")
      if (userType == "EXPERT") {
        getList({
          firstTime: true,
          limit: pageData?.limit * pageData?.page,
          currentPage: 1,
          keyword: search,
        })
      }
    })
    return () => {
      socket.off("internal_user_activity")
    }
  }, [getList, pageData?.limit, pageData?.page, search])
  // Handle window resize
  // Update the fetchMoreData function to prevent multiple fetches
  const fetchMoreData = useCallback(() => {
    if (loading || !hasMore || !TeamsData?.data) return

    setLoading(true)

    try {
      getList({ currentPage: pageData?.page + 1, firstTime: false, keyword: search })
      setPageData((prev) => ({ ...prev, page: prev?.page + 1 }))
    } catch (error) {
      console.error("Error fetching more data:", error)
    } finally {
      setTimeout(() => {
        setLoading(false)
      }, 500)
    }
  }, [loading, hasMore, pageData?.page, getList, setPageData, TeamsData?.data, search])

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      setWindowWidth(width)
      setIsMobile(width < 768)
    }

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize)
      return () => window.removeEventListener("resize", handleResize)
    }
  }, [])

  // Sort data when TeamsData or sortConfig changes
  useEffect(() => {
    if (TeamsData?.data) {
      let sortableData = [...TeamsData.data]

      // Filter by active status if needed
      if (showActiveOnly) {
        sortableData = sortableData.filter((expert) => expert.status === "ACTIVE")
      }

      if (sortConfig.key) {
        sortableData.sort((a, b) => {
          // Handle nested properties
          const aValue = sortConfig.key.includes(".")
            ? sortConfig.key.split(".").reduce((obj, key) => obj?.[key], a)
            : a[sortConfig.key]
          const bValue = sortConfig.key.includes(".")
            ? sortConfig.key.split(".").reduce((obj, key) => obj?.[key], b)
            : b[sortConfig.key]

          // Handle different data types
          if (aValue === null || aValue === undefined) return sortConfig.direction === "ascending" ? 1 : -1
          if (bValue === null || bValue === undefined) return sortConfig.direction === "ascending" ? -1 : 1

          if (typeof aValue === "string" && typeof bValue === "string") {
            return sortConfig.direction === "ascending" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
          }

          return sortConfig.direction === "ascending" ? (aValue > bValue ? 1 : -1) : aValue < bValue ? 1 : -1
        })
      }
      setSortedData(sortableData)
    }
  }, [TeamsData?.data, sortConfig, showActiveOnly])

  const requestSort = (key) => {
    let direction = "ascending"
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending"
    }
    setSortConfig({ key, direction })
  }

  const getSortIcon = (name) => {
    if (sortConfig.key !== name) return <ArrowUpDown className="w-3 h-3 opacity-50" />
    return sortConfig.direction === "ascending" ? (
      <ChevronUp className="w-3 h-3" />
    ) : (
      <ChevronDown className="w-3 h-3" />
    )
  }

  const observer = useRef(null)
  const loadingRef = useRef(null)

  useEffect(() => {
    if (TeamsData?.data?.length >= TeamsData?.pagination?.total) {
      setHasMore(false)
    } else {
      setHasMore(true)
    }
  }, [TeamsData?.data?.length, TeamsData?.pagination?.total])

  // Set up and clean up the IntersectionObserver
  useEffect(() => {
    // Disconnect any existing observer
    if (observer.current) {
      observer.current.disconnect()
    }

    // Function to handle intersection
    const handleIntersection = (entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        fetchMoreData()
      }
    }

    // Create a new observer
    observer.current = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: "100px", // Load a bit before reaching the bottom
      threshold: 0.1, // Trigger when at least 10% of the element is visible
    })

    // Start observing the loading element
    if (loadingRef.current) {
      observer.current.observe(loadingRef.current)
    }

    // Clean up
    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
  }, [fetchMoreData, hasMore, loading])

  const afterSave = () => {
    getList({ currentPage: 1, isEmptylist: false })
    setExpertDetails({})
  }

  const userType = user?.userType?.toLowerCase()

  const deleteFinal = () => {
    dispatch(
      deleteUserActionManager({
        userType,
        data: deleteId,
        cb: () => {
          setDeleteId("")
          getList({ currentPage: 1, isEmptylist: false })
          setExpertDetails({})
        },
      }),
    )
  }
  useEffect(() => {
    getList({ currentPage: 1, firstTime: true })
  }, [])

  const updateStatus = (data) => {
    dispatch(
      updateUserActionManager({
        userType,
        data: {
          _id: data?._id,
          status: data?.status === "ACTIVE" ? "NOT ACTIVE" : "ACTIVE",
        },
        cb: () => getList({ currentPage: 1, isEmptylist: false }),
      }),
    )
  }

  // Mobile card view for each expert
  const renderMobileExpertCard = (expert, index) => (
    <div key={expert._id} className="bg-white rounded-lg shadow-sm mb-3 overflow-hidden">
      <div className="flex items-center justify-between p-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-200">
            <img src={expert.image || "/placeholder.svg"} alt="" className="w-full h-full object-cover" />
          </div>
          <div>
            <button
              onClick={() => setExpertDetails({ data: expert, preview: true, modal: false })}
              className="text-sm font-medium text-blue-600 flex items-center gap-1"
            >
              {expert?.name || "NA"}
            </button>
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
              <Mail className="w-3 h-3" />
              <span className="truncate max-w-[150px]">{expert?.email || "NA"}</span>
            </div>
          </div>
        </div>
        <div className={`flex items-center gap-1 ${expert.isOnline ? "text-green-600" : "text-red-600"}`}>
          <div className={`w-2 h-2 rounded-full ${expert.isOnline ? "bg-green-500" : "bg-red-500"}`} />
          <span className="text-xs font-medium">{expert.isOnline ? "Online" : "Offline"}</span>
        </div>
      </div>

      <div className="p-3 grid grid-cols-2 gap-3">
        <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs text-gray-700">Last Login</span>
          </div>
          <span className="text-xs font-medium text-blue-600">{returnDateWithTime(expert?.lastLogin)}</span>
        </div>

        <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
          <span className="text-xs text-gray-700">Status</span>
          <ToggleSwitch size="small" checked={expert.status === "ACTIVE"} onChange={() => updateStatus(expert)} />
        </div>
      </div>

      <div className="flex border-t border-gray-100">
        <button
          onClick={() => setExpertDetails({ data: expert, preview: true, modal: false })}
          className="flex-1 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center gap-1"
        >
          <User className="w-3.5 h-3.5" />
          View Details
        </button>
        <div className="w-px bg-gray-100"></div>
        <button
          onClick={() =>
            setExpertDetails({
              edit: true,
              modal: true,
              preview: false,
              data: expert,
            })
          }
          className="flex-1 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center gap-1"
        >
          <Activity className="w-3.5 h-3.5" />
          Edit Expert
        </button>
      </div>
    </div>
  )

  // Table headers with sorting
  const tableHeaders = [
    { key: "isOnline", label: "Status" },
    { key: null, label: "Sr." },
    { key: "contactDetails", label: "Contact" },
    { key: null, label: "Image" },
    { key: "name", label: "Name" },
    { key: "lastLogin", label: "Last Login" },
    { key: "email", label: "Email" },
    { key: "number", label: "Number" },
    { key: "userType", label: "Role" },
    { key: null, label: "Actions" },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 text-gray-900 p-2 sm:p-4">
      <div className="rounded-lg overflow-hidden bg-white shadow-xl">
        {deleteId && (
          <DeleteConfirmation
            open={deleteId}
            isDeleteLoading={isDeleteUser}
            onClose={() => setDeleteId("")}
            onConfirm={deleteFinal}
          />
        )}

        <FullScreenModal
          title={"Add Expert"}
          bgContent="dark-bg"
          open={operatorDetail.modal}
          onClose={() => setExpertDetails({})}
          content={() => (
            <FormEditExpert
              onClose={() => {
                afterSave()
                setExpertDetails({})
              }}
              data={operatorDetail?.data}
              close={afterSave}
            />
          )}
        />

        <FullScreenModal
          title={"Personal Details"}
          bgContent="dark-bg"
          size="xl"
          open={operatorDetail.preview}
          onClose={() => setExpertDetails({})}
          content={() => (
            <PersonalCard
              onDeleteClick={(data) => setDeleteId(data)}
              onEditClick={(data) =>
                setExpertDetails({
                  edit: true,
                  modal: true,
                  preview: false,
                  data,
                })
              }
              data={operatorDetail?.data}
              close={() => setExpertDetails({})}
            />
          )}
        />

        <div className="p-3 sm:p-4 border-b border-gray-200 bg-white">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="flex items-center gap-2 w-full justify-center sm:justify-start">
              <button
                onClick={() => dispatch(handlePopupModalAction({ isOpenSidebar: true }))}
                className="mr-4 p-2 hover:text-[#fff]  rounded-md hover:bg-[#0A2547] lg:hidden"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-600">
                {listTitle}
              </h2>
              <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-full">
                <Users className="w-3 h-3 text-blue-500" />
                <span className="text-xs font-medium text-blue-700">Total: {TeamsData?.pagination?.total || 0}</span>
              </div>
            </div>
            <div className="flex flex-row gap-2 w-full justify-between sm:justify-end">
              <div className="relative flex-1 max-w-xs">
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-400"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => {
                    setPageData({ page: 1 })
                    setSearch(e.target.value)
                    getList({ currentPage: 1, firstTime: true, keyword: e.target.value })
                  }}
                  className="w-full h-8 pl-7 pr-7 py-1 text-xs rounded-full border border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
                {search && (
                  <button
                    onClick={() => {
                      setSearch("")
                      getList({ currentPage: 1, firstTime: true, keyword: "" })
                    }}
                    className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                )}
              </div>
              <button
                onClick={() => exportToCSV(sortedData, `expert-list-${new Date().toISOString().slice(0, 10)}.csv`)}
                className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-teal-600 text-white rounded-full text-xs sm:text-sm font-medium flex items-center justify-center gap-1 transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{windowWidth < 640 ? "Export" : "Export CSV"}</span>
              </button>
              <button
                onClick={() => setExpertDetails({ modal: true, edit: false })}
                className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full text-xs sm:text-sm font-medium flex items-center justify-center gap-1 transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{windowWidth < 640 ? "Add" : "Add Expert"}</span>
              </button>
              <div className="flex items-center gap-1 sm:gap-2">
                <span
                  className={`text-xs sm:text-sm ${showActiveOnly ? "text-blue-600 font-medium" : "text-gray-600"}`}
                >
                  {windowWidth < 640 ? "Active Only" : "Show Active Only"}
                </span>
                <ToggleSwitch
                  size={windowWidth < 640 ? "small" : "default"}
                  checked={showActiveOnly}
                  onChange={() => setShowActiveOnly(!showActiveOnly)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Card */}
        <div className="p-2 sm:p-4 bg-white border-b border-gray-100">
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-2 sm:p-3 flex items-center justify-between shadow-sm">
              <div>
                <p className="text-xs sm:text-sm text-blue-600 font-medium">Total</p>
                <h3 className="text-base sm:text-xl font-bold text-gray-800">{TeamsData?.pagination?.total || 0}</h3>
                <p className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">Experts</p>
              </div>
              <div className="bg-blue-100 p-1.5 sm:p-2 rounded-full">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-2 sm:p-3 flex items-center justify-between shadow-sm">
              <div>
                <p className="text-xs sm:text-sm text-green-600 font-medium">Active</p>
                <h3 className="text-base sm:text-xl font-bold text-gray-800">{TeamsData?.pagination?.activeUsers}</h3>
                <p className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">In system</p>
              </div>
              <div className="bg-green-100 p-1.5 sm:p-2 rounded-full">
                <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-2 sm:p-3 flex items-center justify-between shadow-sm">
              <div>
                <p className="text-xs sm:text-sm text-purple-600 font-medium">Online</p>
                <h3 className="text-base sm:text-xl font-bold text-gray-800">{TeamsData?.pagination?.onlineUsers}</h3>
                <p className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">Right now</p>
              </div>
              <div className="bg-purple-100 p-1.5 sm:p-2 rounded-full">
                <div className="relative">
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  <div className="absolute -top-1 -right-1 w-2 h-2 sm:w-2.5 sm:h-2.5 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile view */}
        {isMobile && (
          <div className="p-2">
            {sortedData.map((expert, index) => renderMobileExpertCard(expert, index))}
            <div ref={loadingRef} className="text-center py-4">
              {loading && (
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
              )}
              {!hasMore && TeamsData?.data?.length > 0 && (
                <div className="text-gray-500 text-sm">No more data to load</div>
              )}
              {!loading && TeamsData?.data?.length === 0 && (
                <div className="text-gray-500 text-sm py-4">No data available</div>
              )}
            </div>
          </div>
        )}

        {/* Desktop view */}
        {!isMobile && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  {tableHeaders.map((header, index) => (
                    <th
                      key={index}
                      className={`px-2 sm:px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                        header.key ? "cursor-pointer hover:bg-gray-100" : ""
                      }`}
                      onClick={() => header.key && requestSort(header.key)}
                    >
                      <div className="flex items-center gap-1">
                        {header.label}
                        {header.key && getSortIcon(header.key)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedData.map((expert, index) => (
                  <tr key={expert._id} className="bg-white hover:bg-gray-50 transition-all duration-300">
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div
                          className={`flex items-center gap-1 ${expert.isOnline ? "text-green-600" : "text-red-600"}`}
                        >
                          <div className={`w-2 h-2 rounded-full ${expert.isOnline ? "bg-green-500" : "bg-red-500"}`} />
                          <span className="text-xs font-medium">{expert.isOnline ? "Online" : "Offline"}</span>
                        </div>
                        <ToggleSwitch checked={expert.status === "ACTIVE"} onChange={() => updateStatus(expert)} />
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-gray-700">{index + 1}</td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs">
                      {expert?.contactDetails?.length ? (
                        <span className="px-1 sm:px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-green-100 text-green-800">
                          Yes
                        </span>
                      ) : (
                        <span className="px-1 sm:px-2 py-1 inline-flex text-xs leading-4 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          NA
                        </span>
                      )}
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                      <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-200 transition-transform duration-300 hover:scale-110 hover:border-blue-500">
                        <img src={expert.image || "/placeholder.svg"} alt="" className="w-full h-full object-cover" />
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                      <button
                        onClick={() =>
                          setExpertDetails({
                            data: expert,
                            preview: true,
                            modal: false,
                          })
                        }
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 flex items-center gap-1 hover:underline max-w-[120px] truncate"
                      >
                        <User className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{expert?.name || "NA"}</span>
                      </button>
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                      {returnDateWithTime(expert?.lastLogin)}
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                      <div className="flex items-center gap-1 max-w-[150px]">
                        <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{expert?.email || "NA"}</span>
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                      {expert?.number || "NA"}
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                      <div className="flex items-center gap-1 max-w-[100px]">
                        <Briefcase className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{expert?.userType || "NA"}</span>
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() =>
                            setExpertDetails({
                              edit: true,
                              modal: true,
                              preview: false,
                              data: expert,
                            })
                          }
                          className="p-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
                        >
                          <Activity className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteId(expert._id)}
                          className="p-1 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="w-3.5 h-3.5"
                          >
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div ref={loadingRef} className="text-center py-4">
              {loading && (
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
              )}
              {!hasMore && TeamsData?.data?.length > 0 && (
                <div className="text-gray-500 text-sm">No more data to load</div>
              )}
              {!loading && TeamsData?.data?.length === 0 && (
                <div className="text-gray-500 text-sm py-4">No data available</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ExpertListComponent

